import { motion } from 'framer-motion';
import { Phone, Loader2, AlertCircle, Calendar, Sparkles, Clock, Headphones } from 'lucide-react';
import { Logo } from './Logo';
import { BackgroundPattern } from './BackgroundPattern';

interface LandingPageProps {
  onConnect: () => void;
  isConnecting: boolean;
  error: string | null;
}

export function LandingPage({ onConnect, isConnecting, error }: LandingPageProps) {
  return (
    <div className="landing-page">
      <BackgroundPattern />
      
      <motion.div
        className="landing-content"
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <Logo />
        
        {/* Modern VoxArro Glowing Pulse Orb */}
        <motion.div 
          className="central-orb-wrapper"
          initial={{ scale: 0.85, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.15, duration: 0.8, ease: 'easeOut' }}
        >
          <div className="orb-glow-pulse" />
          <div className="orb-ring ring-outer" />
          <div className="orb-ring ring-middle" />
          <div className="orb-ring ring-inner" />
          <div className="orb-core">
            <div className="orb-wave-bar b1" />
            <div className="orb-wave-bar b2" />
            <div className="orb-wave-bar b3" />
            <div className="orb-wave-bar b4" />
            <div className="orb-wave-bar b5" />
          </div>
        </motion.div>

        <motion.div
          className="hero-section"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.6 }}
        >
          <h2 className="hero-title">Speak with Veronica</h2>
          <p className="hero-subtitle">
            Your 24/7 AI-powered voice receptionist for appointment bookings,
            <br />
            stylist schedules, pricing, and salon inquiries.
          </p>
        </motion.div>

        <motion.div
          className="cta-section"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.45, duration: 0.4 }}
        >
          <button
            className="connect-button"
            onClick={onConnect}
            disabled={isConnecting}
          >
            {isConnecting ? (
              <>
                <Loader2 className="button-icon spinning" />
                <span>Connecting to Veronica...</span>
              </>
            ) : (
              <>
                <Phone className="button-icon" />
                <span>Start Conversation</span>
              </>
            )}
          </button>

          {error && (
            <motion.div
              className="error-message"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <AlertCircle size={16} />
              <span>{error}</span>
            </motion.div>
          )}
        </motion.div>

        <motion.div
          className="features-section"
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.6 }}
        >
          <div className="feature-capsule">
            <div className="feature-icon-wrapper">
              <Calendar size={16} />
            </div>
            <span className="feature-text">Book Appointments</span>
          </div>
          <div className="feature-capsule">
            <div className="feature-icon-wrapper">
              <Sparkles size={16} />
            </div>
            <span className="feature-text">Services & Pricing</span>
          </div>
          <div className="feature-capsule">
            <div className="feature-icon-wrapper">
              <Clock size={16} />
            </div>
            <span className="feature-text">Stylist Availability</span>
          </div>
          <div className="feature-capsule">
            <div className="feature-icon-wrapper">
              <Headphones size={16} />
            </div>
            <span className="feature-text">Salon Support</span>
          </div>
        </motion.div>

        <footer className="landing-footer">
          <p>Powered by <strong>Veronica</strong> • AI Salon Receptionist</p>
        </footer>
      </motion.div>
    </div>
  );
}

